import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { trackPixel } from '../../analytics/metaPixel';
import { Button, Screen } from '../../ui';

/**
 * Where Stripe Checkout sends someone back to (infra/lambda/handlers/stripe.ts, for
 * checkouts started here). Before these existed, a paying customer landed on an S3 XML
 * error — charged, granted, and told nothing (ISSUES-TABLE #22).
 *
 * These pages grant nothing. The webhook owns entitlement, so a page that is never
 * reached, or reached twice, cannot change what anyone has paid for.
 */

/** The monthly price, for the ad pixel's Subscribe value. Keep in step with Stripe. */
const PRICE = { value: 20, currency: 'USD' } as const;

export function BillingSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  /*
   * A top-up comes back here too, marked by the server (`&kind=topup`, stripe.ts). It is not a
   * subscription: reporting it to the ad pixel as a Subscribe would count every top-up as a new
   * subscriber, and "You're subscribed" would greet somebody who already was.
   */
  const topup = params.get('kind') === 'topup';

  useEffect(() => {
    if (topup) return;
    /*
     * Once per checkout, not per page load: a refresh or a back-button return would
     * otherwise report a second subscription. Without a session id this cannot be told
     * apart from someone typing the URL, so nothing is sent.
     */
    if (!sessionId || sessionId.includes('{')) return;
    const key = `saddle_subscribe_sent:${sessionId}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      /* private mode: send it; a duplicate is better than never knowing */
    }
    trackPixel('Subscribe', PRICE);
  }, [sessionId, topup]);

  if (topup) {
    return (
      <Screen title="Credits on their way">
        <p className="type-body">
          Thanks — your payment went through. Your credits can take a minute to show up.
        </p>
        <Button title="See my credits" onPress={() => navigate('/credits')} />
      </Screen>
    );
  }

  return (
    <Screen title="You're subscribed">
      <p className="type-body">
        Thanks — your payment went through. Your credits can take a minute to show up.
      </p>
      <Button title="Build an app" onPress={() => navigate('/new')} />
      <Button title="See my credits" variant="ghost" onPress={() => navigate('/credits')} />
    </Screen>
  );
}

export function BillingCancelled() {
  const navigate = useNavigate();
  return (
    <Screen title="No charge made">
      <p className="type-body">You left checkout before paying, so nothing was charged.</p>
      <Button title="Back to credits" onPress={() => navigate('/credits')} />
    </Screen>
  );
}
